import React, { useState } from "react";
import PropTypes from "prop-types";
import Styled from "styled-components";
import {
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from "@material-ui/core";
import {
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from "@material-ui/icons";
import { useTranslation } from "react-i18next";

import Button from "../../waybee-ui/Button";
import { passwordPolicyErrors, passwordPolicyHint } from "../../utils/passwordPolicy";
import SupportContact from "./SupportContact";

const Title = Styled.h3`
  font-size: 28px;
  font-weight: 300;
  color: #00303b;
  margin: 16px 0 12px;
  line-height: 1.43;
`;

const Form = Styled.form`
  width: 100%;
`;

function ResetPassword(props) {
  const {
    closeResetPassword,
    identifier,
    token,
    newPassword,
    confirmationPassword,
    onChange,
    onSubmit,
    loading,
    error,
    errorField,
    supportPhone,
    supportEmail
  } = props;
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const policyErrors = passwordPolicyErrors(newPassword, t);

  return (
    <>
      <Button
        color="secondary"
        startIcon={<ArrowBackIcon />}
        size="large"
        to="/"
        onClick={closeResetPassword}
      />
      <Title>{t("login.resetTitle")}</Title>
      <Typography variant="subtitle1" gutterBottom>
        {t("login.resetHint")}
      </Typography>
      <Form
        noValidate
        onSubmit={e => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <TextField
          margin="normal"
          required
          fullWidth
          id="reset-identifier"
          label={t("login.identifier")}
          name="identifier"
          value={identifier}
          onChange={onChange("identifier")}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="reset-token"
          label={t("login.resetToken")}
          name="token"
          value={token}
          onChange={onChange("token")}
          autoFocus
          error={errorField === "token"}
          helperText={errorField === "token" ? error : undefined}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="reset-new-password"
          label={t("login.newPassword")}
          name="newPassword"
          type={showPassword ? "text" : "password"}
          value={newPassword}
          onChange={onChange("newPassword")}
          helperText={passwordPolicyHint(t)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t("login.togglePassword")}
                  onMouseDown={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="reset-confirmation"
          label={t("login.confirmPassword")}
          name="confirmationPassword"
          type={showPassword ? "text" : "password"}
          value={confirmationPassword}
          onChange={onChange("confirmationPassword")}
          error={errorField === "confirmationPassword"}
          helperText={
            errorField === "confirmationPassword" ? error : undefined
          }
        />
        {error && !errorField ? <FormHelperText error>{error}</FormHelperText> : null}
        {!error && policyErrors.length ? (
          <FormHelperText error>{policyErrors[0]}</FormHelperText>
        ) : null}
        <Button
          className="main-button"
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? t("login.resetSubmitting") : t("login.resetSubmit")}
        </Button>
      </Form>
      <SupportContact phone={supportPhone} email={supportEmail} />
    </>
  );
}

ResetPassword.propTypes = {
  closeResetPassword: PropTypes.func.isRequired,
  identifier: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  newPassword: PropTypes.string.isRequired,
  confirmationPassword: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  errorField: PropTypes.string,
  supportPhone: PropTypes.string,
  supportEmail: PropTypes.string
};

ResetPassword.defaultProps = {
  loading: false,
  error: "",
  errorField: "",
  supportPhone: "",
  supportEmail: ""
};

export default ResetPassword;
